import type { BlendMode, LayerColorLabel } from "./Layer";
import type { RasterLayer } from "./Layer";
import type { LayerStyles } from "./LayerStyle";

export type AntiAliasMode = "none" | "sharp" | "crisp" | "strong" | "smooth";

/**
 * Photoshop Warp Text styles. Exactly mirrors the 15 styles in the PS dialog
 * plus `none`. Each style chooses a different mapping from (x, y) → (x', y').
 */
export type WarpTextStyle =
  | "none"
  | "arc"
  | "arc-lower"
  | "arc-upper"
  | "arch"
  | "bulge"
  | "shell-lower"
  | "shell-upper"
  | "flag"
  | "wave"
  | "fish"
  | "rise"
  | "fisheye"
  | "inflate"
  | "squeeze"
  | "twist";

export interface WarpTextParams {
  style: WarpTextStyle;
  /** "horizontal" warps along x, "vertical" warps along y. */
  orientation: "horizontal" | "vertical";
  /** Bend, in [-1..1] (UI shows -100..100). */
  bend: number;
  /** Horizontal distortion in [-1..1] (UI shows -100..100). */
  horizontalDistortion: number;
  /** Vertical distortion in [-1..1] (UI shows -100..100). */
  verticalDistortion: number;
}

export const DEFAULT_WARP: WarpTextParams = {
  style: "none",
  orientation: "horizontal",
  bend: 0.5,
  horizontalDistortion: 0,
  verticalDistortion: 0,
};

/**
 * A styled run within the layer's text string.
 * `start` and `end` are character offsets (end is exclusive, like
 * String.slice). A run may carry a partial override — only the properties
 * present in the object differ from the layer's baseline values.
 */
export interface TextRun {
  start: number;
  end: number;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: number;
  fontStyle?: "normal" | "italic";
  color?: { r: number; g: number; b: number; a: number };
}

export interface TypeLayer {
  readonly id: string;
  readonly kind: "type";
  readonly name: string;
  readonly visible: boolean;
  readonly opacity: number;
  readonly fillOpacity: number;
  readonly blendMode: BlendMode;
  readonly x: number;
  readonly y: number;
  readonly lockTransparency: boolean;
  readonly lockImage: boolean;
  readonly lockPosition: boolean;
  readonly lockAll: boolean;
  readonly clippedToBelow: boolean;
  readonly colorLabel?: LayerColorLabel;
  readonly styles?: LayerStyles;
  /**
   * Linked layers — IDs of layers linked to this one for joint transforms.
   * Mirrors LayerBase.linkedLayerIds (Layer.ts) — TypeLayer declares its own
   * field set rather than extending LayerBase, so this has to be repeated
   * here too for type layers to participate in linked-layer moves (i56).
   */
  readonly linkedLayerIds?: readonly string[];

  // Type-specific properties
  readonly text: string;
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly fontWeight: number;
  readonly fontStyle: "normal" | "italic";
  readonly textAlign: "left" | "center" | "right" | "justify";
  readonly textBaseline: "top" | "middle" | "bottom";
  readonly color: { r: number; g: number; b: number; a: number };
  readonly leading: number;
  readonly tracking: number;
  readonly baselineShift: number;
  readonly underline: boolean;
  readonly strikethrough: boolean;
  readonly antiAlias: AntiAliasMode;
  readonly fauxBold: boolean;
  readonly fauxItalic: boolean;
  readonly allCaps: boolean;
  readonly smallCaps: boolean;
  readonly superscript: boolean;
  readonly subscript: boolean;

  // Paragraph text bounds — null means point text (unbounded)
  readonly textBounds?: { x: number; y: number; w: number; h: number } | null;

  /**
   * Paragraph panel fields (#i100) — Photoshop's Left/Right/First-Line
   * Indent and Space Before/After, in document-space pixels. Optional (like
   * the other fields added to this interface after its initial fields, e.g.
   * `antiAlias`/`fauxBold` above) so pre-existing `TypeLayer` object literals
   * elsewhere (pxa.ts's PSD/PXA (de)serializer, test fixtures) that predate
   * #i100 keep type-checking without every call site needing an update.
   * createTypeLayer() always fills them in (defaulting to 0), and every
   * reader defaults via `?? 0` — absent means "no indent/spacing", a no-op.
   * Consumed by Canvas2DRenderer's paragraph-text (`textBounds` != null)
   * wrapping/line-start logic only — point text ignores these for now.
   */
  readonly leftIndent?: number;
  readonly rightIndent?: number;
  readonly firstLineIndent?: number;
  readonly spaceBefore?: number;
  readonly spaceAfter?: number;

  // Per-character style runs — empty or absent means the whole layer uses the
  // baseline properties (fontSize, fontFamily, etc.). Only runs that carry
  // at least one override need be present.
  readonly textRuns?: readonly TextRun[];

  // Warp Text parameters (style: "none" means no warp applied)
  readonly warp?: WarpTextParams;

  /**
   * Vertical (CJK) orientation — glyphs flow top-to-bottom within a column
   * and columns advance right-to-left, Photoshop's Vertical Type Tool
   * convention (#i15). Falsy/absent means the standard horizontal layout.
   */
  readonly vertical?: boolean;

  // Rasterized fallback
  readonly rasterized?: RasterLayer;
}

export function createTypeLayer(params: {
  id?: string;
  name?: string;
  text?: string;
  fontFamily?: string;
  fontSize?: number;
  color?: { r: number; g: number; b: number; a: number };
  x?: number;
  y?: number;
  textBounds?: { x: number; y: number; w: number; h: number } | null;
  vertical?: boolean;
  /**
   * Type options-bar fields (#i94) — TypeTool stashes the user's chosen
   * Anti-alias / Alignment / Faux Bold / Faux Italic on the tool instance;
   * callers pass them through here so new type layers actually adopt the
   * options bar's current settings instead of always falling back to the
   * hardcoded defaults below.
   */
  antiAlias?: AntiAliasMode;
  textAlign?: "left" | "center" | "right" | "justify";
  fauxBold?: boolean;
  fauxItalic?: boolean;
  // Paragraph panel fields (#i100) — see TypeLayer.leftIndent etc. above.
  leftIndent?: number;
  rightIndent?: number;
  firstLineIndent?: number;
  spaceBefore?: number;
  spaceAfter?: number;
}): TypeLayer {
  return {
    id: params.id ?? crypto.randomUUID(),
    kind: "type",
    name: params.name ?? "Type Layer",
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal" as BlendMode,
    x: params.x ?? 0,
    y: params.y ?? 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,

    text: params.text ?? "Hello",
    fontFamily: params.fontFamily ?? "Arial",
    fontSize: params.fontSize ?? 36,
    fontWeight: 400,
    fontStyle: "normal",
    textAlign: params.textAlign ?? "left",
    textBaseline: "top",
    color: params.color ?? { r: 0, g: 0, b: 0, a: 1 },
    leading: 1.2,
    tracking: 0,
    baselineShift: 0,
    underline: false,
    strikethrough: false,
    antiAlias: params.antiAlias ?? "smooth",
    fauxBold: params.fauxBold ?? false,
    fauxItalic: params.fauxItalic ?? false,
    allCaps: false,
    smallCaps: false,
    superscript: false,
    subscript: false,
    textBounds: params.textBounds ?? null,
    warp: { ...DEFAULT_WARP },
    vertical: params.vertical ?? false,
    leftIndent: params.leftIndent ?? 0,
    rightIndent: params.rightIndent ?? 0,
    firstLineIndent: params.firstLineIndent ?? 0,
    spaceBefore: params.spaceBefore ?? 0,
    spaceAfter: params.spaceAfter ?? 0,
  };
}

/**
 * Warp a single (x, y) point per the given warp params. `x01` and `y01` are
 * the *normalized* coordinates within the text bounding box, both in [0..1].
 * Returns the warped (dx, dy) displacement in the same normalized space, which
 * the caller multiplies by the text bounds and adds to the original point.
 *
 * This is a faithful approximation of Photoshop's Warp Text math; pixel-perfect
 * parity would require the proprietary PS curve splines.
 */
export function applyWarpToPoint(
  warp: WarpTextParams,
  x01: number,
  y01: number,
): { x: number; y: number } {
  if (warp.style === "none") return { x: x01, y: y01 };

  // Centered coordinates: [-1..1].
  const cx = (warp.orientation === "horizontal") ? x01 * 2 - 1 : y01 * 2 - 1;
  const cy = (warp.orientation === "horizontal") ? y01 * 2 - 1 : x01 * 2 - 1;
  const bend = warp.bend;
  const hDist = warp.horizontalDistortion;
  const vDist = warp.verticalDistortion;

  let warpedX = cx;
  let warpedY = cy;
  const TWO_PI = Math.PI * 2;

  switch (warp.style) {
    case "arc":
      warpedY = cy + bend * (1 - cx * cx);
      break;
    case "arc-lower":
      warpedY = cy + bend * (cy + 1) * 0.5 * (1 - cx * cx);
      break;
    case "arc-upper":
      warpedY = cy - bend * (1 - cy) * 0.5 * (1 - cx * cx);
      break;
    case "arch":
      warpedY = cy + bend * Math.cos(cx * Math.PI / 2);
      break;
    case "bulge":
      warpedY = cy + bend * Math.sign(cy) * (1 - cx * cx);
      break;
    case "shell-lower":
      warpedY = cy + bend * (cy + 1) * 0.5 * Math.cos(cx * Math.PI / 2);
      break;
    case "shell-upper":
      warpedY = cy - bend * (1 - cy) * 0.5 * Math.cos(cx * Math.PI / 2);
      break;
    case "flag":
      warpedY = cy + bend * 0.5 * Math.sin(cx * Math.PI);
      break;
    case "wave":
      warpedY = cy + bend * 0.5 * Math.sin(cx * Math.PI * 2);
      break;
    case "fish":
      warpedY = cy + bend * 0.5 * Math.sign(cx) * (1 - Math.abs(cx));
      break;
    case "rise":
      warpedY = cy + bend * cx;
      break;
    case "fisheye": {
      // Spherical bulge — both x and y bow outward at the center.
      const r2 = cx * cx + cy * cy;
      const k = 1 + bend * (1 - Math.min(1, r2));
      warpedX = cx * k;
      warpedY = cy * k;
      break;
    }
    case "inflate":
      warpedY = cy * (1 + bend * (1 - cx * cx));
      break;
    case "squeeze":
      warpedX = cx * (1 + bend * (1 - cy * cy));
      break;
    case "twist": {
      // Rotate every (cx, cy) around the center by angle proportional to its
      // distance from center, scaled by bend.
      const r = Math.hypot(cx, cy);
      const theta = bend * Math.PI * r;
      const cs = Math.cos(theta);
      const sn = Math.sin(theta);
      warpedX = cx * cs - cy * sn;
      warpedY = cx * sn + cy * cs;
      break;
    }
  }

  // Horizontal / vertical distortion: tilts the warp box.
  warpedX += hDist * cy * 0.5;
  warpedY += vDist * cx * 0.5;

  // De-center back to [0..1].
  if (warp.orientation === "horizontal") {
    return { x: (warpedX + 1) / 2, y: (warpedY + 1) / 2 };
  }
  // Vertical orientation: swap result axes.
  return { x: (warpedY + 1) / 2, y: (warpedX + 1) / 2 };
}

export function isTypeLayer(layer: any): layer is TypeLayer {
  return layer?.kind === "type";
}

/**
 * Apply a font-size override to `[selStart, selEnd)` within `runs`,
 * returning a new runs array. Overlapping runs are split as needed so
 * the selected range gets its own run with the new size.
 *
 * If the selection covers the entire text (or runs is empty / falsy)
 * returns null to signal "apply to the whole layer instead."
 */
export function applyFontSizeToRuns(
  text: string,
  runs: readonly TextRun[] | undefined,
  selStart: number,
  selEnd: number,
  newSize: number,
): TextRun[] | null {
  // Clamp selection to valid range.
  const s = Math.max(0, Math.min(selStart, text.length));
  const e = Math.max(s, Math.min(selEnd, text.length));
  if (s === e) return null; // empty selection — nothing to do
  if (s === 0 && e === text.length) return null; // whole text — caller applies at layer level

  // Flatten existing runs into a per-char array (only fontSize for now).
  const existing: Array<TextRun | null> = new Array(text.length).fill(null);
  for (const r of runs ?? []) {
    for (let i = r.start; i < r.end; i++) {
      if (i < text.length) existing[i] = r;
    }
  }

  // Build a new merged runs array by scanning the whole string.
  const out: TextRun[] = [];
  let i = 0;
  while (i < text.length) {
    const inSel = i >= s && i < e;
    const baseRun = existing[i];
    // Collect the longest contiguous stretch that shares the same "identity"
    // (in selection + same base run reference).
    let j = i + 1;
    while (j < text.length) {
      if ((j >= s && j < e) !== inSel) break;
      if (existing[j] !== baseRun) break;
      j++;
    }
    const runProps: TextRun = { start: i, end: j };
    // Copy non-fontSize props from base run.
    if (baseRun) {
      if (baseRun.fontFamily !== undefined) runProps.fontFamily = baseRun.fontFamily;
      if (baseRun.fontWeight !== undefined) runProps.fontWeight = baseRun.fontWeight;
      if (baseRun.fontStyle !== undefined) runProps.fontStyle = baseRun.fontStyle;
      if (baseRun.color !== undefined) runProps.color = baseRun.color;
    }
    // Apply new size only to chars inside the selection.
    if (inSel) {
      runProps.fontSize = newSize;
    } else if (baseRun?.fontSize !== undefined) {
      runProps.fontSize = baseRun.fontSize;
    }
    // Only emit runs that carry at least one override.
    const hasOverride =
      runProps.fontSize !== undefined ||
      runProps.fontFamily !== undefined ||
      runProps.fontWeight !== undefined ||
      runProps.fontStyle !== undefined ||
      runProps.color !== undefined;
    if (hasOverride) out.push(runProps);
    i = j;
  }

  return out;
}
